import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaTrash, FaUpload } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import AdminNav from '../../components/AdminNav';
import api from '../../utils/api';
import './ProductForm.css';

const ProductForm = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    images: []
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      const product = response.data;
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category?._id || '',
        stock: product.stock,
        images: product.images || []
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to load product');
    }
  };

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 
                        'video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Invalid file type. Only images (JPEG, PNG, GIF, WebP) and videos (MP4, MPEG, MOV, WebM) are allowed.');
      return;
    }

    // Validate file sizes (50MB max)
    const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('File size exceeds 50MB limit.');
      return;
    }

    setSelectedFiles(prev => [...prev, ...files]);
    
    // Create preview URLs
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    setError('');
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Revoke object URL to prevent memory leaks
    URL.revokeObjectURL(previewUrls[index]);
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const encodedUrl = encodeURIComponent(imageUrl);
      await api.delete(`/products/${id}/media/${encodedUrl}`);
      
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter(img => img !== imageUrl)
      }));
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('stock', formData.stock);
      
      // Add existing images as JSON
      formDataToSend.append('images', JSON.stringify(formData.images));
      
      // Add new files
      selectedFiles.forEach(file => {
        formDataToSend.append('files', file);
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      };

      if (isEditMode) {
        await api.put(`/products/${id}`, formDataToSend, config);
      } else {
        await api.post('/products', formDataToSend, config);
      }

      // Clean up preview URLs
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.response?.data?.message || 'Failed to save product');
      setLoading(false);
    }
  };

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      <AdminNav />
      <div className="product-form-page">
        <div className="form-header">
        <button onClick={() => navigate('/admin/products')} className="btn-back">
          <FaArrowLeft /> Back to Products
        </button>
        <h1>{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="product-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter product name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="5"
              placeholder="Enter product description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price ($) *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="stock">Stock Count *</label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                required
                min="0"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Product Media</h2>
          
          {/* Existing Images */}
          {isEditMode && formData.images.length > 0 && (
            <div className="existing-media-section">
              <h3>Current Images/Videos</h3>
              <div className="media-grid">
                {formData.images.map((imageUrl, index) => (
                  <div key={index} className="media-item">
                    {imageUrl.includes('/videos/') ? (
                      <video src={imageUrl} controls className="media-preview" />
                    ) : (
                      <img src={imageUrl} alt={`Product ${index + 1}`} className="media-preview" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeExistingImage(imageUrl)}
                      className="btn-delete-media"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Upload */}
          <div className="upload-section">
            <h3>Upload New Media</h3>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/mpeg,video/quicktime,video/webm"
                onChange={handleFileSelect}
                className="file-input"
              />
              <label htmlFor="file-upload" className="file-input-label">
                <FaUpload /> Choose Images or Videos
              </label>
              <p className="file-input-hint">
                Max 10 files, 50MB each. Supports: JPEG, PNG, GIF, WebP, MP4, MPEG, MOV, WebM
              </p>
            </div>

            {/* Preview Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="selected-files-section">
                <h4>Selected Files ({selectedFiles.length})</h4>
                <div className="media-grid">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="media-item">
                      {file.type.startsWith('video/') ? (
                        <video src={previewUrls[index]} controls className="media-preview" />
                      ) : (
                        <img src={previewUrls[index]} alt={file.name} className="media-preview" />
                      )}
                      <div className="file-info">
                        <p className="file-name">{file.name}</p>
                        <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="btn-delete-media"
                        title="Remove"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p>{uploadProgress}% uploaded</p>
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/admin/products')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="btn btn-primary">
            <FaSave /> {loading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Create Product')}
          </button>
        </div>
      </form>
      </div>
    </>
  );
};

export default ProductForm;
